/**
 * Category utility functions for consistent category display across the app.
 *
 * Uses the centralized <Icon> component for visual display.
 * See src/components/shared/Icon.js for available icon names.
 */

import React from 'react';
import Icon, { icons } from '../components/shared/Icon';
import { kindColor, kindOfCategory, kindOfTypes, PIN_GLYPHS } from './mapPins';
import { SPINE_SUBCATEGORY_TYPES, SPINE_FAMILY_TYPES } from '../config/spineCategoryTypes';

/**
 * Category to Icon name mapping (for <Icon name={...}> component)
 */
const CATEGORY_ICON_MAP = {
  // Food & Drink
  food: 'forkKnife',
  restaurant: 'forkKnife',
  cafe: 'coffee',
  coffee: 'coffee',
  bar: 'beer',
  bakery: 'coffee',

  // Culture & Arts
  culture: 'museum',
  museum: 'museum',
  arts: 'paintBrush',
  art_gallery: 'paintBrush',
  gallery: 'paintBrush',
  historical: 'museum',
  library: 'bookOpen',

  // Outdoor & Nature
  outdoor: 'tree',
  park: 'tree',
  nature: 'tree',
  beach: 'beach',
  hiking: 'mountain',
  garden: 'leaf',

  // Entertainment
  entertainment: 'theater',
  theater: 'theater',
  cinema: 'film',
  movie_theater: 'film',
  music: 'musicalNote',
  concert: 'microphone',
  comedy: 'theater',

  // Sports & Recreation
  sports: 'trophy',
  gym: 'dumbbell',
  swimming: 'swimmer',
  golf: 'trophy',

  // Shopping
  shopping: 'shoppingBag',
  shopping_mall: 'shoppingBag',
  mall: 'shoppingCart',
  market: 'shoppingCart',

  // Education
  education: 'graduationCap',
  school: 'graduationCap',
  university: 'graduationCap',

  // Lifestyle
  nightlife: 'moon',
  night_club: 'moon',
  spa: 'spa',
  wellness: 'spa',

  // Family
  family: 'userGroup',
  kids: 'baby',
  playground: 'stroller',

  // Attractions & Travel
  attraction: 'building',
  attractions: 'mapPin',
  sightseeing: 'camera',
  landmark: 'building',
  tourist_attraction: 'star',
  hotel: 'hotel',
  lodging: 'hotel',
  accommodation: 'hotel',

  // Events
  event: 'ticket',

  // Transport
  transport: 'car',

  // Custom-activity form categories (see CustomActivityForm.js)
  free_time: 'sun',

  // Tours & Experiences
  walking_tour: 'walking',
  // A curated walk on a day (docs/WALKS_RUNBOOK.md); the map's walk card uses it too.
  walk: 'walking',
  tourism_experience: 'compass',
  viator_experience: 'ticket',

  // Misc
  place: 'mapPin',
  custom: 'sparkles',

  // Default
  other: 'target',
  activity: 'target'
};

/**
 * Get the Icon component name for a category.
 * @param {string} category - Category name
 * @returns {string} Icon name (key for <Icon name={...}>)
 */
export const getCategoryIconName = (category) => {
  if (!category) return CATEGORY_ICON_MAP.other;
  return CATEGORY_ICON_MAP[category.toLowerCase()] || CATEGORY_ICON_MAP.other;
};

/**
 * CategoryIcon — renders the appropriate <Icon> for a category string.
 * @param {Object} props
 * @param {string} props.category - Category name
 * @param {string} [props.className='w-5 h-5'] - Tailwind classes for sizing/color
 */
export const CategoryIcon = ({ category, className = 'w-5 h-5', ...props }) => (
  <Icon name={getCategoryIconName(category)} className={className} {...props} />
);

/**
 * Get an inline SVG markup string for a category icon.
 * Used in Google Maps InfoWindow HTML templates where React components
 * cannot be rendered.
 * @param {string} category - Category name
 * @param {number} [size=20] - Icon size in pixels
 * @param {string} [color='#666'] - Icon color
 * @returns {string} SVG markup string
 */
export const getCategoryIconSvg = (category, size = 20, color = '#666') => {
  const iconName = getCategoryIconName(category);
  const icon = icons[iconName];
  if (!icon) return '';

  const viewBox = icon.viewBox || '0 0 24 24';
  const isFilled = icon.fill === true;
  const stroke = isFilled ? 'none' : color;
  const fill = isFilled ? color : 'none';
  const sw = isFilled ? '' : ' stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"';

  if (icon.paths) {
    const pathsStr = icon.paths
      .map(p => `<path d="${p.d}"${p.opacity ? ` opacity="${p.opacity}"` : ''}/>`)
      .join('');
    return `<svg width="${size}" height="${size}" viewBox="${viewBox}" fill="${fill}" stroke="${stroke}"${sw} style="display:inline-block;vertical-align:middle;">${pathsStr}</svg>`;
  }

  return `<svg width="${size}" height="${size}" viewBox="${viewBox}" fill="${fill}" stroke="${stroke}"${sw} style="display:inline-block;vertical-align:middle;"><path d="${icon.d}"/></svg>`;
};

/**
 * Get display name for a category (capitalize first letter)
 * @param {string} category - Category name
 * @returns {string} Display name
 */
export const getCategoryDisplayName = (category) => {
  if (!category) return 'Activity';
  return category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Get display name for a category
 * @param {string} category - Category name
 * @returns {string} Display name (e.g., "Food")
 */
export const getCategoryLabel = (category) => {
  return getCategoryDisplayName(category);
};

/**
 * Get all available categories with their icon names
 * @returns {Array} Array of { value, label, iconName } objects
 */
export const getAllCategories = () => {
  const uniqueCategories = [
    'food', 'cafe', 'culture', 'museum', 'park',
    'entertainment', 'music', 'shopping', 'education',
    'sports', 'nightlife', 'family', 'sightseeing',
    'attractions', 'wellness'
  ];

  return uniqueCategories.map(cat => ({
    value: cat,
    label: getCategoryDisplayName(cat),
    iconName: getCategoryIconName(cat)
  }));
};

/**
 * Get the colour for a category or Google type. Every map colours a place by
 * its FAMILY (Kurtis, 2026-10-02; src/utils/mapPins.js): six families instead
 * of 19 hues, a gold star for landmarks and an ink square for where you sleep.
 * Map lists and legends that sit beside the pins read the same answer.
 * @param {string} category - Category name or type
 * @returns {string} Hex color code
 */
export const getCategoryColor = (category) => kindColor(kindOfCategory(category) || 'other');

/**
 * Infer category from Google Places types array
 * @param {Array<string>} types - Google Places types
 * @returns {string} Inferred category name
 */
export const inferCategoryFromTypes = (types = []) => {
  if (!types || types.length === 0) return 'other';

  // Google type → display category
  const typeToCategory = {
    restaurant: 'restaurant', food: 'restaurant', meal_takeaway: 'restaurant',
    cafe: 'cafe', coffee_shop: 'cafe',
    bar: 'bar', night_club: 'night_club', pub: 'bar', wine_bar: 'bar',
    bakery: 'bakery',
    museum: 'museum',
    art_gallery: 'art_gallery', cultural_center: 'museum',
    movie_theater: 'movie_theater', performing_arts_theater: 'entertainment',
    park: 'park', playground: 'park', national_park: 'park',
    hiking_area: 'park', campground: 'park', beach: 'beach',
    zoo: 'zoo', aquarium: 'aquarium',
    amusement_park: 'amusement_park',
    adventure_sports_center: 'entertainment', amusement_center: 'entertainment',
    recreation_center: 'entertainment', sports_complex: 'entertainment',
    trampoline_park: 'entertainment', escape_room: 'entertainment',
    video_arcade: 'entertainment', karaoke: 'entertainment',
    bowling_alley: 'entertainment', casino: 'entertainment',
    shopping_mall: 'shopping_mall',
    store: 'store', clothing_store: 'store', book_store: 'store',
    electronics_store: 'store',
    lodging: 'lodging', hotel: 'lodging',
    gym: 'gym', fitness_center: 'gym',
    spa: 'spa', beauty_salon: 'spa',
    library: 'library',
    tourist_attraction: 'tourist_attraction',
  };

  // Primary type (first in array) gets priority
  const primaryCategory = typeToCategory[types[0]];
  if (primaryCategory) return primaryCategory;

  // Fall back to weighted scoring
  const scores = {};
  for (const type of types) {
    const cat = typeToCategory[type];
    if (cat) {
      scores[cat] = (scores[cat] || 0) + 1;
    }
  }

  let best = 'other';
  let bestScore = 0;
  for (const [cat, score] of Object.entries(scores)) {
    if (score > bestScore) {
      bestScore = score;
      best = cat;
    }
  }
  return best;
};

/**
 * SVG icon paths for map markers, keyed by Google Places type.
 * Each entry has { color, icon } where icon is an SVG fragment for a 24x24 viewBox.
 * Only the GLYPH is read now; the colour comes from the place's family
 * (getCategoryMarkerIcon). The old colours stay as a record of the per-type look.
 */
const CATEGORY_MARKER_ICONS = {
  // Food & Drink
  restaurant: { color: '#ef4444', icon: '<path d="M9 7v4h2v6h2v-6h2V7c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2z" fill="white"/><path d="M7 7v10h2V7H7z" fill="white"/>' },
  cafe: { color: '#92400e', icon: '<path d="M8 9h8v2a4 4 0 01-8 0V9zm0-2h8V6H8v1zm2 8h4v2h-4v-2z" fill="white"/>' },
  bar: { color: '#7c3aed', icon: '<path d="M8 6l1 5h6l1-5H8zm2 7h4l1 4H9l1-4z" fill="white"/>' },
  bakery: { color: '#f59e0b', icon: '<path d="M12 6c-2.2 0-4 1.8-4 4v1h8v-1c0-2.2-1.8-4-4-4zm-4 7v4h8v-4H8z" fill="white"/>' },
  // Culture & Entertainment
  museum: { color: '#3b82f6', icon: '<path d="M12 6l-6 3v1h12v-1l-6-3zm-5 5v5h2v-5H7zm4 0v5h2v-5h-2zm4 0v5h2v-5h-2zm-8 6v1h10v-1H7z" fill="white"/>' },
  art_gallery: { color: '#ec4899', icon: '<path d="M7 7h10v10H7V7zm2 2v6h6V9H9z" fill="white"/><rect x="10" y="10" width="4" height="4" fill="white"/>' },
  movie_theater: { color: '#6366f1', icon: '<path d="M8 7v10h8V7H8zm2 2h4v6h-4V9z" fill="white"/><circle cx="9" cy="8" r="1" fill="white"/><circle cx="15" cy="8" r="1" fill="white"/><circle cx="9" cy="16" r="1" fill="white"/><circle cx="15" cy="16" r="1" fill="white"/>' },
  night_club: { color: '#8b5cf6', icon: '<path d="M12 6l2 4h-4l2-4zm-3 5h6l1 6H8l1-6z" fill="white"/>' },
  // Nature & Outdoors
  park: { color: '#22c55e', icon: '<path d="M12 6l-4 6h3v5h2v-5h3l-4-6z" fill="white"/>' },
  zoo: { color: '#16a34a', icon: '<circle cx="10" cy="10" r="2" fill="white"/><circle cx="14" cy="10" r="2" fill="white"/><path d="M9 14c0 1.5 1.3 3 3 3s3-1.5 3-3H9z" fill="white"/>' },
  aquarium: { color: '#0891b2', icon: '<path d="M8 12c0-2 2-4 4-4s4 2 4 4-2 4-4 4-4-2-4-4z" fill="white"/><circle cx="14" cy="11" r="1" fill="black" fill-opacity=".45"/>' },
  amusement_park: { color: '#f97316', icon: '<circle cx="12" cy="12" r="5" fill="none" stroke="white" stroke-width="1.5"/><path d="M12 7v10M7 12h10" stroke="white" stroke-width="1.5"/>' },
  // Shopping & Services
  shopping_mall: { color: '#f472b6', icon: '<path d="M9 8h6l1 8H8l1-8zm1-2h4v2h-4V6z" fill="white"/>' },
  store: { color: '#f472b6', icon: '<path d="M9 8h6l1 8H8l1-8zm1-2h4v2h-4V6z" fill="white"/>' },
  // Accommodation
  lodging: { color: '#0ea5e9', icon: '<path d="M7 13v4h10v-4H7zm0-1h10V9a2 2 0 00-2-2h-6a2 2 0 00-2 2v3z" fill="white"/>' },
  hotel: { color: '#0ea5e9', icon: '<path d="M7 13v4h10v-4H7zm0-1h10V9a2 2 0 00-2-2h-6a2 2 0 00-2 2v3z" fill="white"/>' },
  // Health & Wellness
  gym: { color: '#14b8a6', icon: '<path d="M6 11h2v2H6v-2zm10 0h2v2h-2v-2zm-8 0h8v2H8v-2z" fill="white"/>' },
  spa: { color: '#a855f7', icon: '<path d="M12 7c-1.5 0-3 1-3 3 0 1.5 1.5 3 3 5 1.5-2 3-3.5 3-5 0-2-1.5-3-3-3z" fill="white"/>' },
  // Education
  library: { color: '#64748b', icon: '<path d="M7 7h10v10H7V7zm2 2v6h2v-6H9zm4 0v6h2v-6h-2z" fill="white"/>' },
  // Attractions
  tourist_attraction: { color: '#eab308', icon: '<path d="M12 6l1.5 3.5 3.5.5-2.5 2.5.5 3.5-3-1.5-3 1.5.5-3.5L7 10l3.5-.5L12 6z" fill="white"/>' },
};

const DEFAULT_MARKER_ICON = { color: '#6b7280', icon: '<circle cx="12" cy="10" r="3" fill="white"/><path d="M12 14l-3 4h6l-3-4z" fill="white"/>' };

/**
 * Get SVG marker icon data for a Google Places types array.
 * Used by GoogleMap.js and MapTab.js for map marker rendering.
 * @param {Array<string>} types - Google Places types array
 * @returns {{ color: string, icon: string, kind: string, shape: 'disc'|'square' }}
 *   Family colour, glyph, the kind (family key, 'stay' or 'landmark') and shape
 */
export const getCategoryMarkerIcon = (types) => {
  const typesList = types || [];
  // The glyph says café or bakery; the colour says the family (mapPins.js).
  // Both come from the SAME type — the first one we know — or a place typed
  // ['night_club', 'tourist_attraction'] wore the shows colour with a star.
  const kind = kindOfTypes(typesList);
  if (kind === 'stay') return { color: kindColor(kind), icon: PIN_GLYPHS.bed, kind, shape: 'square' };
  if (kind === 'landmark') return { color: kindColor(kind), icon: PIN_GLYPHS.star, kind, shape: 'disc' };
  // An 'other' place (no type has a family, e.g. a spa) keeps its own glyph.
  const sameKind = (type) => (kind === 'other' ? !kindOfCategory(type) : kindOfCategory(type) === kind);
  const glyphType = typesList.find(type => CATEGORY_MARKER_ICONS[type] && sameKind(type));
  const icon = glyphType ? CATEGORY_MARKER_ICONS[glyphType].icon : DEFAULT_MARKER_ICON.icon;
  return { color: kindColor(kind), icon, kind, shape: 'disc' };
};

// Spine places (Discover Sights) carry a category_id instead of Google types;
// src/config/spineCategoryTypes.js maps it to a type the glyphs and labels
// above read (shared with the server's saved-place readers).
/**
 * @param {string|null} categoryId e.g. 'culture.museum'
 * @returns {string[]} e.g. ['museum'] — never empty; a sight by default
 */
export const typesForSpineCategory = (categoryId) => {
  if (!categoryId) return ['tourist_attraction'];
  const type = SPINE_SUBCATEGORY_TYPES[categoryId] || SPINE_FAMILY_TYPES[categoryId.split('.')[0]];
  return [type || 'tourist_attraction'];
};
